import React, { useState, useEffect } from 'react'
import Modal from '../../Modal'
import logo from '../../assets/images/matcha-logo.svg'
import './AnimatedTutorial.css'

const AnimatedTutorial = ({
  mode,
  gridSize = { rows: 2, cols: 3 },
  constraints = { moves: null, timeLimit: null },
  onClose
}) => {
  // Generate cards based on grid size
  const totalCards = gridSize.rows * gridSize.cols
  const pairs = totalCards / 2

  const initialCards = Array.from({ length: totalCards }, (_, i) => {
    const pairIndex = Math.floor(i / 2)
    const emojis = ['🎨', '🚀', '🌟', '🎯', '🔥', '💎', '🎪', '🎭']
    return {
      id: i,
      value: emojis[pairIndex % emojis.length],
      isFlipped: false,
      isMatched: false,
      isUnmatched: false
    }
  })

  const [demoCards, setDemoCards] = useState(initialCards)
  const [demoStep, setDemoStep] = useState(0)
  const [moveCount, setMoveCount] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState(constraints.timeLimit || 60)

  // Animation sequence
  useEffect(() => {
    // Medium mode skips preview (no initial card reveal)
    const skipPreview = mode === 'medium'

    const steps = skipPreview ? [
      // Medium mode: Start with cards hidden
      () => {
        setDemoCards(cards => cards.map(c => ({ ...c, isFlipped: false, isUnmatched: false })))
        setMoveCount(0)
      },
    ] : [
      // Step 0: Initial preview - show all cards (Easy/Hard/Memory/Story)
      () => {
        setDemoCards(cards => cards.map(c => ({ ...c, isFlipped: true, isUnmatched: false })))
      },
      // Step 1: Hide all cards after preview
      () => {
        setDemoCards(cards => cards.map(c => ({ ...c, isFlipped: false, isUnmatched: false })))
        setMoveCount(0)
      },
    ]

    // Add the main animation steps (same for all modes)
    const mainSteps = [
      // FAILED MATCH - Click first card
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 0 ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
        if (constraints.moves) setMoveCount(1)
      },
      // FAILED MATCH - Click second card (different emoji)
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 2 ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
        if (constraints.moves) setMoveCount(2)
      },
      // FAILED MATCH - Show red (unmatched state)
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 0 || i === 2 ? { ...c, isUnmatched: true } : c
        ))
      },
      // FAILED MATCH - Flip both cards back
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 0 || i === 2 ? { ...c, isFlipped: false, isUnmatched: false } : c
        ))
      },
      // SUCCESSFUL MATCH - Click first card
      () => {
        const firstMatchIndex = demoCards.findIndex((c, i) => i > 0 && c.value === demoCards[0].value)
        setDemoCards(cards => cards.map((c, i) =>
          i === 0 ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
        if (constraints.moves) setMoveCount(3)
      },
      // SUCCESSFUL MATCH - Click matching card
      () => {
        const firstMatchIndex = demoCards.findIndex((c, i) => i > 0 && c.value === demoCards[0].value)
        setDemoCards(cards => cards.map((c, i) =>
          i === firstMatchIndex ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
        if (constraints.moves) setMoveCount(4)
      },
      // SUCCESSFUL MATCH - Mark both as matched (yellow)
      () => {
        setDemoCards(cards => cards.map((c, i) => {
          if (i === 0 || c.value === demoCards[0].value) {
            return { ...c, isMatched: true }
          }
          return c
        }))
      },
      // Wait before reset
      () => {},
      // Reset for next loop
      () => {
        setDemoCards(initialCards)
        setDemoStep(0)
        setMoveCount(0)
        setTimeRemaining(constraints.timeLimit || 60)
      }
    ]

    // Combine preview steps (if any) with main steps
    const allSteps = [...steps, ...mainSteps]

    const interval = setInterval(() => {
      if (demoStep < allSteps.length) {
        allSteps[demoStep]()
        setDemoStep(prev => prev + 1)
      }
    }, 750) // 750ms per step

    return () => clearInterval(interval)
  }, [demoStep, constraints.moves, constraints.timeLimit])

  // Timer countdown for hard mode
  useEffect(() => {
    // Count down after preview phase and before final reset step
    const skipPreview = mode === 'medium'
    const previewSteps = skipPreview ? 1 : 2
    const totalSteps = previewSteps + 9 // 9 main steps

    if (constraints.timeLimit && demoStep >= previewSteps && demoStep < totalSteps - 1) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => Math.max(0, prev - 1))
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [constraints.timeLimit, demoStep, mode])

  const gridClass = `animated-demo-grid grid-${gridSize.rows}x${gridSize.cols}`

  // Get mode-specific explanation text
  const getModeExplanation = () => {
    switch (mode) {
      case 'easy':
        return 'All cards are shown at the start. Memorize their positions, then match all pairs to win!'
      case 'medium':
        return 'Cards are hidden from the start. Use your memory to find matching pairs!'
      case 'hard':
        return `Beat the clock! You have ${constraints.timeLimit} seconds to match all pairs before time runs out.`
      case 'memory':
        if (constraints.moves && constraints.timeLimit) {
          return `Complete challenge! Match all pairs within ${constraints.moves} moves AND ${constraints.timeLimit} seconds.`
        } else if (constraints.moves) {
          return `Match all pairs within ${constraints.moves} moves. Plan your moves carefully!`
        } else if (constraints.timeLimit) {
          return `Match all pairs within ${constraints.timeLimit} seconds. Speed is key!`
        }
        return 'Match all pairs to win!'
      case 'story':
        if (constraints.moves && constraints.timeLimit) {
          return `Beat this level by matching all pairs within ${constraints.moves} moves AND ${constraints.timeLimit} seconds!`
        } else if (constraints.moves) {
          return `Complete this level within ${constraints.moves} moves!`
        } else if (constraints.timeLimit) {
          return `Complete this level within ${constraints.timeLimit} seconds!`
        }
        return 'Match all pairs to complete this level!'
      default:
        return 'Match all pairs to win!'
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="animated-tutorial">
        <h2 className="tutorial-title">How to Play</h2>
        <p className="tutorial-subtitle">{getModeExplanation()}</p>

        {/* How Matching Works */}
        <div className="tutorial-instructions">
          <p><strong>How it works:</strong></p>
          <ul>
            <li>Click two cards to flip them over</li>
            <li>If they match, they stay flipped (yellow)</li>
            <li>If they don't match, they turn red and flip back</li>
            <li>Match all pairs to win!</li>
          </ul>
        </div>

        {/* Constraints Display */}
        {(constraints.moves || constraints.timeLimit) && (
          <div className="demo-constraints">
            {constraints.moves && (
              <span className={`constraint-badge ${moveCount >= constraints.moves * 0.8 ? 'warning' : ''}`}>
                Moves: {moveCount}/{constraints.moves}
              </span>
            )}
            {constraints.timeLimit && (
              <span className={`constraint-badge ${timeRemaining <= 10 ? 'warning' : ''}`}>
                Time: {timeRemaining}s
              </span>
            )}
          </div>
        )}

        {/* Animated Card Grid */}
        <div className={gridClass}>
          {demoCards.map((card) => (
            <div
              key={card.id}
              className={`demo-card ${card.isFlipped ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''} ${card.isUnmatched ? 'unmatched' : ''}`}
            >
              <div className="demo-card-inner">
                <div className="demo-card-front">
                  <img src={logo} alt="Matcha Logo" className="demo-card-logo" />
                </div>
                <div className="demo-card-back">{card.value}</div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={onClose} className="tutorial-close-btn">
          Got it! Start Game
        </button>
      </div>
    </Modal>
  )
}

export default AnimatedTutorial
